import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { 
  X, Target, User, Calendar, Flag, FileText, Save, Plus, CheckCircle, 
  ExclamationCircle, Info, ArrowUturnLeftIcon 
} from "lucide-react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

const API_URL = "https://ims.holdthechild.net/api";

const categoryOptions = [
    { value: "performance", label: "Performance" },
    { value: "quality", label: "Quality" },
    { value: "efficiency", label: "Efficiency" },
    { value: "growth", label: "Growth" },
];

const timeframeOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "biannual", label: "Bi-Annual" },
    { value: "annual", label: "Annual" },
];

const KPIForm = ({ onClose, onSuccess, initialData = {}, isEditMode = false }) => {
    const { register, handleSubmit, reset, setValue, control, formState: { errors }, watch } = useForm();
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingStaff, setFetchingStaff] = useState(true);
    const [error, setError] = useState("");
    const [selectedCategory, setSelectedCategory] = useState(
        categoryOptions.find(opt => opt.value === (initialData.category || "performance")) || categoryOptions[0]
    );
    const [selectedTimeframe, setSelectedTimeframe] = useState(
        timeframeOptions.find(opt => opt.value === (initialData.timeframe || "quarterly")) || timeframeOptions[0]
    );
    const [selectedStaff, setSelectedStaff] = useState(null);
    
    const modalRef = useRef(null);
    const reportTitleRef = useRef(null);
    const staffRef = useRef(null);

    // Watch description field for length
    const description = watch("description") || "";

    // Fetch staff list on component mount
    useEffect(() => {
        const fetchStaff = async () => {
            try {
                setFetchingStaff(true);
                const token = localStorage.getItem("token");
                
                if (!token) {
                    throw new Error("Authentication token not found");
                }
                
                const response = await axios.get(`${API_URL}/staff`, {
                    headers: { Authorization: `Bearer ${token}` } }
                );
                
                // Handle different response structures
                const staffData = response.data.data || response.data.staff || response.data;
                setStaffList(Array.isArray(staffData) ? staffData : []);
            } catch (err) {
                console.error("Failed to fetch staff:", err);
                setError("Failed to load staff list");
                toast.error("Failed to load staff list");
            } finally {
                setFetchingStaff(false);
            }
        };

        fetchStaff();
    }, []);

    // Pre-fill form when editing
    useEffect(() => {
        if (isEditMode && initialData) {
            setValue("title", initialData.title || "");
            setValue("staffId", initialData.staffId || initialData.staff?._id || "");
            setValue("targetValue", initialData.targetValue || "");
            setValue("currentValue", initialData.currentValue || 0);
            setValue("description", initialData.description || "");
            
            const category = categoryOptions.find(opt => opt.value === (initialData.category || "performance"));
            setSelectedCategory(category || categoryOptions[0]);
            
            const timeframe = timeframeOptions.find(opt => opt.value === (initialData.timeframe || "quarterly"));
            setSelectedTimeframe(timeframe || timeframeOptions[0]);
            
            // Set selected staff if staffId is available
            if (initialData.staffId || initialData.staff?._id) {
                const staffId = initialData.staffId || initialData.staff?._id;
                const staff = staffList.find(s => s._id === staffId);
                if (staff) {
                    setSelectedStaff({
                        value: staff._id,
                        label: `${staff.firstName} ${staff.lastName} - ${staff.position || staff.jobTitle || staff.department || "Staff"}`
                    });
                }
            }
        }
    }, [isEditMode, initialData, setValue, staffList]);

    useEffect(() => {
        setValue("category", selectedCategory.value);
    }, [selectedCategory, setValue]);

    useEffect(() => {
        setValue("timeframe", selectedTimeframe.value);
    }, [selectedTimeframe, setValue]);

    const handleStaffChange = (selected) => {
        setSelectedStaff(selected);
        setValue("staffId", selected ? selected.value : "");
    };

    const onSubmit = async (data) => {
        setLoading(true);
        setError("");
        
        try {
            const token = localStorage.getItem("token");
            
            if (!token) {
                throw new Error("Authentication token not found");
            }
            
            // Find the selected staff object
            const selectedStaffObj = staffList.find(staff => staff._id === data.staffId);
            
            // Prepare form data with both staffId and staff name
            const formData = {
                ...data,
                category: selectedCategory.value,
                timeframe: selectedTimeframe.value,
                staff: selectedStaffObj ? `${selectedStaffObj.firstName} ${selectedStaffObj.lastName}` : ""
            };
            
            console.log("Submitting form data:", formData);
            
            let response;
            if (isEditMode && initialData._id) {
                response = await axios.put(
                    `${API_URL}/kpis/${initialData._id}`, 
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("KPI updated successfully");
            } else {
                response = await axios.post(
                    `${API_URL}/kpis`, 
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("KPI created successfully");
                onSuccess(response.data);
            }
            onClose();
        } catch (err) {
            console.error("KPI submission failed:", err);
            const errorMessage = err.response?.data?.message || err.response?.data?.error || "Something went wrong";
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Format staff list for react-select
    const staffOptions = staffList.map(staff => ({
        value: staff._id,
        label: `${staff.firstName} ${staff.lastName} - ${staff.position || staff.jobTitle || staff.department || "Staff"}`
    }));

    // Enhanced keyboard navigation
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [onClose]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <div
                    ref={modalRef}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
                >
                    {/* Enhanced Header with Orange Background */}
                    <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
                        <div className="flex items-center justify-between px-8 py-6">
                            <div className="flex items-center space-x-4">
                                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                                    <Target className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-black">
                                        {isEditMode ? "Edit KPI" : "Add New KPI"}
                                    </h2>
                                    <p className="text-lg text-black-100 mt-1">
                                        Performance Management
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Enhanced Body */}
                    <div className="flex-grow px-8 py-6 overflow-y-auto">
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                                <div className="flex items-start space-x-3">
                                    <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-red-700">
                                        <p className="font-medium">Error</p>
                                        <p className="mt-1">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} id="kpi-form" className="space-y-6">
                            <div ref={reportTitleRef}>
                                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                    <span>KPI Title</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Required
                                    </span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Target className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        {...register("title", { 
                                            required: "KPI title is required",
                                            minLength: { value: 3, message: "KPI title must be at least 3 characters" }
                                        })}
                                        type="text"
                                        className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                        placeholder="Enter KPI title"
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-gray-500">
                                        3-100 characters • Be specific and descriptive
                                    </p>
                                    <span className={`text-xs ${
                                        errors.title ? 'text-red-500' : 'text-gray-400'
                                    }`}>
                                        {errors.title ? 'Required' : 'Optional'}
                                    </span>
                                </div>
                                {errors.title && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                        <ExclamationCircleIcon className="w-4 h-4" />
                                        <span>{errors.title.message}</span>
                                    </p>
                                )}
                            </div>

                            <div ref={staffRef}>
                                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                    <span>Assigned Staff</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Required
                                    </span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    {fetchingStaff ? (
                                        <div className="pl-10 pr-3 py-3 w-full border border-gray-300 rounded-xl bg-gray-50 flex items-center">
                                            <svg className="animate-spin h-5 w-5 text-orange-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span className="text-sm text-gray-500">Loading staff...</span>
                                        </div>
                                    ) : (
                                        <Controller
                                            name="staffId"
                                            control={control}
                                            rules={{ required: "Staff selection is required" }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    value={selectedStaff}
                                                    onChange={(selected) => {
                                                        field.onChange(selected ? selected.value : "");
                                                        handleStaffChange(selected);
                                                    }}
                                                    options={staffOptions}
                                                    isClearable
                                                    placeholder="Select a staff member"
                                                    className="rounded-xl"
                                                    styles={{
                                                        control: (base, state) => ({
                                                            ...base,
                                                            borderRadius: '0.75rem',
                                                            minHeight: '44px',
                                                            paddingLeft: '42px',
                                                            borderColor: state.isFocused || errors.staffId ? '#F97316' : '#D1D5DB',
                                                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                            '&:hover': {
                                                                borderColor: '#F97316'
                                                            }
                                                        }),
                                                        placeholder: (base) => ({
                                                            ...base,
                                                            color: '#9CA3AF'
                                                        }),
                                                        singleValue: (base) => ({
                                                            ...base,
                                                            color: '#1F2937'
                                                        }),
                                                        option: (base, state) => ({
                                                            ...base,
                                                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                            color: state.isSelected ? 'white' : '#1F2937',
                                                            '&:hover': {
                                                                backgroundColor: '#FFEDD5',
                                                                color: '#1F2937'
                                                            }
                                                        }),
                                                        dropdownIndicator: (base) => ({
                                                            ...base,
                                                            color: '#6B7280',
                                                            '&:hover': {
                                                                color: '#F97316'
                                                            }
                                                        }),
                                                        indicatorSeparator: (base) => ({
                                                            ...base,
                                                            backgroundColor: '#D1D5DB'
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            borderRadius: '0.75rem',
                                                            border: '1px solid #E5E7EB'
                                                        })
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                </div>
                                {errors.staffId && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                        <ExclamationCircleIcon className="w-4 h-4" />
                                        <span>{errors.staffId.message}</span>
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Target Value</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Flag className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("targetValue", { 
                                                required: "Target value is required",
                                                min: { value: 1, message: "Target must be at least 1" }
                                            })}
                                            type="number"
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.targetValue ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            placeholder="Target"
                                        />
                                    </div>
                                    {errors.targetValue && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.targetValue.message}</span>
                                        </p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Current Value</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            Optional
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Flag className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            {...register("currentValue", { 
                                                min: { value: 0, message: "Current value cannot be negative" }
                                            })}
                                            type="number"
                                            className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.currentValue ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            placeholder="Current"
                                        />
                                    </div>
                                    {errors.currentValue && (
                                        <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                                            <ExclamationCircleIcon className="w-4 h-4" />
                                            <span>{errors.currentValue.message}</span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Category</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Target className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <Select
                                            value={selectedCategory}
                                            onChange={setSelectedCategory}
                                            options={categoryOptions}
                                            className="w-full rounded-xl"
                                            placeholder="Select Category"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    minHeight: '44px',
                                                    paddingLeft: '42px',
                                                    borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                    '&:hover': {
                                                        borderColor: '#F97316'
                                                    }
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: '#9CA3AF'
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: '#1F2937'
                                                }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                    '&:hover': {
                                                        backgroundColor: '#FFEDD5',
                                                        color: '#1F2937'
                                                    }
                                                }),
                                                dropdownIndicator: (base) => ({
                                                    ...base,
                                                    color: '#6B7280',
                                                    '&:hover': {
                                                        color: '#F97316'
                                                    }
                                                }),
                                                indicatorSeparator: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#D1D5DB'
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    border: '1px solid #E5E7EB'
                                                })
                                            }}
                                        />
                                    </div>
                                    <input type="hidden" {...register("category")} value={selectedCategory.value} />
                                </div>
                                
                                <div>
                                    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                        <span>Timeframe</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Required
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Calendar className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <Select
                                            value={selectedTimeframe}
                                            onChange={setSelectedTimeframe}
                                            options={timeframeOptions}
                                            className="w-full rounded-xl"
                                            placeholder="Select Timeframe"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    minHeight: '44px',
                                                    paddingLeft: '42px',
                                                    borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                    '&:hover': {
                                                        borderColor: '#F97316'
                                                    }
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: '#9CA3AF'
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: '#1F2937'
                                                }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                    '&:hover': {
                                                        backgroundColor: '#FFEDD5',
                                                        color: '#1F2937'
                                                    }
                                                }),
                                                dropdownIndicator: (base) => ({
                                                    ...base,
                                                    color: '#6B7280',
                                                    '&:hover': {
                                                        color: '#F97316'
                                                    }
                                                }),
                                                indicatorSeparator: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#D1D5DB'
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    border: '1px solid #E5E7EB'
                                                })
                                            }}
                                        />
                                    </div>
                                    <input type="hidden" {...register("timeframe")} value={selectedTimeframe.value} />
                                </div>
                            </div>

                            <div>
                                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                                    <span>Description</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        Optional
                                    </span>
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                        <FileText className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        {...register("description")}
                                        rows={4}
                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none"
                                        placeholder="Describe this KPI..."
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-gray-500">
                                        {description.length > 0 ? 'Add details about this KPI' : 'Provide additional context if needed'}
                                    </p>
                                    <span className="text-xs text-gray-400">
                                        {description.length}/500
                                    </span>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Enhanced Footer */}
                    <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                    <Info className="w-4 h-4 text-orange-500" />
                                    <span>All fields marked as required must be completed</span>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    form="kpi-form"
                                    disabled={loading}
                                    className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                                        loading
                                            ? 'bg-orange-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl'
                                    }`}
                                >
                                    {loading ? (
                                        <div className="flex items-center space-x-3">
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>
                                                {isEditMode ? "Updating..." : "Creating..."}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-2">
                                            <Save className="w-5 h-5" />
                                            <span>{isEditMode ? "Update KPI" : "Create KPI"}</span>
                                        </div>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default KPIForm;